/**
 * AnalyticsSkeleton Components
 *
 * Reusable skeleton loading states for analytics sections.
 * Matches the actual layout dimensions to prevent layout shift.
 */

import { Box, Grid, Paper, Skeleton } from '@mui/material';

/**
 * Reusable stat card skeleton
 */
export function StatCardSkeleton() {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="80%" height={40} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="40%" height={20} />
    </Paper>
  );
}

/**
 * Reusable chart skeleton
 */
export function ChartSkeleton({ height = 300 }: { height?: number }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Skeleton variant="text" width={150} height={28} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 1 }} />
    </Paper>
  );
}

/**
 * Full section skeleton (4 stat cards + 2 charts)
 * Matches the layout of revenue/bookings sections
 */
export function SectionSkeleton() {
  return (
    <Box>
      {/* Section Title */}
      <Skeleton variant="text" width={180} height={32} sx={{ mb: 3 }} />

      {/* Stat Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <StatCardSkeleton />
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <ChartSkeleton height={300} />
        </Grid>
        <Grid item xs={12} lg={5}>
          <ChartSkeleton height={300} />
        </Grid>
      </Grid>
    </Box>
  );
}

/**
 * Full page skeleton for initial load
 * Shows 4 section skeletons matching the actual page layout
 */
export function AnalyticsPageSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {[1, 2, 3, 4].map((i) => (
        <SectionSkeleton key={i} />
      ))}
    </Box>
  );
}
